import React, { useEffect, useState, Fragment, useRef, forwardRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import T from 'prop-types';
import * as R from 'ramda';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  GenderSelector,
  UserAvatar,
  View,
  Text,
  TouchableItem,
  Separator,
  InputWithTitleAndValidation,
} from '../../../ReusableComponents';
import { userOperations } from '../../../../store/userInfo';
import s from './styles';
import { darkBlueHeaderStyle } from '../../../../styles/headerStyle';
import { colors, rs, dimensions } from '../../../../styles';
import { getParamOr } from '../../../../utils/navHelper';
import { NavigationService } from '../../../../services';
import strings from '../../../../localization';
import { MIN_USER_AGE } from '../../../../constants/user';
import { day } from '../../../../utils/dateHelper';
import screens from '../../../../navigation/screens';
import { getAppLocaleCode } from '../../../../store/app/selectors';
import { isURL } from '../../../../utils/stringHelper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { isWeb } from '../../../../utils/detectDevice';
import { DescriptionInput } from '../../LotsGroup/EditLotsGroup/components';
import { userLocationsSelectors } from '../../../../store/userLocations';
import { getCurrentTheme } from '../../../../templates';

const TIMER_INITIAL_VALUE = 30;

const EditUserInfo = ({
  userInfo,
  updateUserInfo,
  sendEmailVerification,
  navigation,
  route,
  appLocaleCode,
  hasPrimaryLocation,
  primaryLocation,
}) => {
  const isNewUser = route.params?.isNewUser ?? false;
  const [userAvatar, setUserAvatar] = useState(isNewUser ? '' : userInfo.profile_image);
  const [userAvatarWeb, setUserAvatarWeb] = useState('');
  const [userName, setUserName] = useState(isNewUser ? '' : userInfo.name);
  const [userBio, setUserBio] = useState(userInfo.bio ?? '');
  const birthdayDefaultValue = (userInfo.birth_date && new Date(userInfo.birth_date)) || null;
  const [birthday, setBirthday] = useState(birthdayDefaultValue);
  const [gender, setGender] = useState(userInfo.gender);
  const [email, setEmail] = useState(userInfo.email);
  const [isInvalidEmail, setInvalidEmail] = useState(false);
  const [foundationName, setFoundationName] = useState(userInfo.foundation_name ?? '');
  const [foundationDescription, setFoundationDescription] = useState(
    userInfo.foundation_description ?? '',
  );
  const fileInputRef = useRef(null);

  const currentTheme = getCurrentTheme();

  useEffect(() => {
    if (!isNewUser) {
      navigation.setParams({
        isValid: true,
      });
    }
  }, []);

  useEffect(() => {
    const isValid = !isInvalidEmail && !!userName && userName.trim().length > 1;

    navigation.setParams({ updateInfo: updateInfo, isValid });
  }, [
    userName,
    userAvatar,
    email,
    gender,
    birthday,
    foundationName,
    foundationDescription,
    userBio,
  ]);

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <TouchableItem ref={ref} onPress={onClick} style={s.nicknameButton}>
      <Text medium>{strings.user_info.date_of_birth}</Text>
      {birthday ? (
        <Text color={currentTheme.colors.secondary} style={rs.marginTop}>
          {day(birthday)}
        </Text>
      ) : (
        <Text color={colors.grayBorder} style={rs.marginTop}>
          {strings.user_info.date_of_birth_placeholder}
        </Text>
      )}
    </TouchableItem>
  ));

  // This is a workaround for android devices
  // because onKeyPress event handler doesn't work as expected
  const onChangeUserName = (value) => {
    // check if it's backspace key pressed
    if (value.length < userName.length) {
      setUserName(value);
      return;
    }

    const userNameRegExp = new RegExp(/^[a-zA-Z\u0590-\u05fe\s]+$/);
    const [key] = R.difference(value, userName);
    const isValidKey = userNameRegExp.test(key);

    if (isValidKey) {
      setUserName(value);
    }
  };

  const onChangeUserBio = (bio) => {
    setUserBio(bio);
  };

  const updateInfo = async () => {
    const name = userName.trim();
    const bio = userBio.trim();

    await updateUserInfo({
      userName: name,
      userAvatar: isWeb && userAvatarWeb ? userAvatarWeb : isURL(userAvatar) ? null : userAvatar,
      gender,
      birthDate: birthday,
      bio,
      ...(userInfo.is_foundation ? { foundationName, foundationDescription } : {}),
    });

    NavigationService.goBack();
  };

  const openImagePicker = () => {
    fileInputRef.current?.click();
  };

  const onOpenEditNickname = () => {
    NavigationService.navigate(screens.EditNickname);
  };

  const onOpenUserLocations = () => {
    NavigationService.navigate(screens.UserLocations);
  };

  const onOpenEditEmail = () => {
    NavigationService.navigate(screens.AddEmail);
  };

  const handleConfirm = (date) => {
    // https://github.com/mmazzarolo/react-native-modal-datetime-picker#why-does-the-picker-show-up-twice-on-android
    // datepicker should be hidden before any other action on Android to prevent unnecessary duplicated appearance
    setBirthday(date);
  };

  const onChangeEmail = async (value) => {
    let reg = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    const isInvalid = !reg.test(value) && !R.isEmpty(value);

    setInvalidEmail(isInvalid);
    setEmail(value);
  };

  const onPressVerifyEmail = async () => {
    await sendEmailVerification(userInfo.email);
  };

  const setImageForWeb = (file) => {
    setUserAvatarWeb(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      setUserAvatar(reader.result);
    };
  };

  const maxUserBirthDate = moment().subtract(MIN_USER_AGE, 'years').toDate();

  return (
    <KeyboardAwareScrollView
      extraScrollHeight={dimensions.doubleMedium}
      enableResetScrollToCoords={false}
      keyboardShouldPersistTaps="handled"
      style={[rs.backgroundWhite, rs.webContainer, s.webContainer]}
    >
      <View style={s.avatarWithInfoContainer}>
        <TouchableItem onPress={openImagePicker}>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={() => setImageForWeb(fileInputRef.current?.files[0])}
          />
          <UserAvatar big uri={userAvatar} withEditLabel={!!userAvatar} />
        </TouchableItem>
      </View>

      <View style={[rs.paddingHorizontal, rs.bigPaddingTop, rs.backgroundGray]}>
        <InputWithTitleAndValidation
          title={`*${strings.user_info.display_name}`}
          autoCapitalize="none"
          minLength={5}
          underlineColorAndroid="transparent"
          placeholder={strings.login.type}
          maxLength={25}
          onChangeText={onChangeUserName}
          value={userName}
          errorText={!userName.length && strings.user_info.name_validation_error}
          useForcedRTL
        />
      </View>

      <Separator small color={colors.grayLight} />

      <TouchableItem onPress={onOpenEditNickname} style={s.nicknameButton}>
        <Text medium>{strings.user_info.username}</Text>
        {userInfo.nickname ? (
          <Text color={currentTheme.colors.secondary} style={rs.marginTop}>
            {userInfo.nickname}
          </Text>
        ) : (
          <Text color={colors.grayBorder} style={rs.marginTop}>
            {strings.user_info.username_placeholder}
          </Text>
        )}
      </TouchableItem>

      <View style={rs.paddingHorizontal}>
        <Separator />
      </View>

      <TouchableItem onPress={onOpenUserLocations} style={s.nicknameButton}>
        <Text medium>{strings.settings.your_addresses}</Text>
        {hasPrimaryLocation ? (
          <Text color={currentTheme.colors.secondary} style={rs.marginTop}>
            {primaryLocation?.formatted_address || primaryLocation?.city}
          </Text>
        ) : (
          <Text color={colors.grayBorder} style={rs.marginTop}>
            {strings.locations.add_address}
          </Text>
        )}
      </TouchableItem>

      <View style={rs.paddingHorizontal}>
        <Separator />
      </View>

      <View style={s.nicknameButton}>
        <Text medium>{strings.user_info.user_bio_title}</Text>
        <DescriptionInput
          containerStyle={s.bioInput}
          value={userBio}
          placeholder={strings.user_info.user_bio_placeholder}
          onChangeText={onChangeUserBio}
        />
      </View>

      <View style={rs.paddingHorizontal}>
        <Separator />
      </View>

      <DatePicker
        locale={appLocaleCode}
        value={birthday || maxUserBirthDate}
        onChange={handleConfirm}
        customInput={<ExampleCustomInput />}
        showYearDropdown
        maxDate={maxUserBirthDate}
        yearDropdownItemNumber={50}
        scrollableYearDropdown
        withPortal
        portalId="root-portal"
      />

      <View style={rs.paddingHorizontal}>
        <Separator />
      </View>

      <View style={[rs.paddingHorizontal, rs.bigPaddingVertical, rs.backgroundWhite]}>
        <GenderSelector selectedGender={gender} onSelect={setGender} />
      </View>

      <View style={rs.paddingHorizontal}>
        <Separator />
      </View>
      {/*<View style={[rs.paddingHorizontal, rs.bigMarginTop, rs.backgroundWhite]}>*/}
      {/*  <InputWithTitleAndValidation*/}
      {/*    autoCapitalize="none"*/}
      {/*    title={strings.user_info.email_address}*/}
      {/*    maxLength={30}*/}
      {/*    minLength={5}*/}
      {/*    placeholder={strings.user_info.email_placeholder}*/}
      {/*    onChangeText={onChangeEmail}*/}
      {/*    value={email}*/}
      {/*    underlineColorAndroid="transparent"*/}
      {/*    errorText={*/}
      {/*      email && email.length && isInvalidEmail*/}
      {/*        ? strings.user_info.email_validation_error*/}
      {/*        : null*/}
      {/*    }*/}
      {/*  />*/}
      {/*</View>*/}

      <TouchableItem onPress={onOpenEditEmail} style={s.nicknameButton}>
        <Fragment>
          <Text medium>{strings.user_info.email_address}</Text>
          {userInfo.email ? (
            <Text color={currentTheme.colors.secondary} style={rs.marginTop}>
              {userInfo.email}
            </Text>
          ) : (
            <Text color={colors.grayBorder} style={rs.marginTop}>
              {strings.user_info.email_placeholder}
            </Text>
          )}
          {!!userInfo.email && !userInfo.is_email_verified && (
            <Text color={colors.red} style={s.emailValidationText}>
              {strings.user_info.verify_email_validation}{' '}
              <Text
                onPress={onPressVerifyEmail}
                style={[s.emailValidationButtonText, { color: currentTheme.colors.secondary }]}
              >
                {strings.login.send_code_again}
              </Text>
            </Text>
          )}
        </Fragment>
      </TouchableItem>

      {!!userInfo.is_foundation && (
        <Fragment>
          <Separator small color={colors.grayLight} />

          <View style={[rs.paddingHorizontal, rs.bigPaddingTop, rs.backgroundGray]}>
            <InputWithTitleAndValidation
              title={strings.user_info.foundation_name}
              autoCapitalize="none"
              minLength={5}
              underlineColorAndroid="transparent"
              placeholder={strings.user_info.foundation_name_placeholder}
              maxLength={25}
              onChangeText={setFoundationName}
              value={foundationName}
              useForcedRTL
            />
          </View>

          <Separator small color={colors.grayLight} />

          <View style={[rs.paddingHorizontal, rs.bigPaddingTop, rs.backgroundGray]}>
            <InputWithTitleAndValidation
              title={strings.user_info.foundation_description}
              autoCapitalize="none"
              underlineColorAndroid="transparent"
              placeholder={strings.user_info.foundation_description_placeholder}
              maxLength={120}
              onChangeText={setFoundationDescription}
              value={foundationDescription}
              useForcedRTL
            />
          </View>

          <Separator small color={colors.grayLight} />
        </Fragment>
      )}
    </KeyboardAwareScrollView>
  );
};

EditUserInfo.propTypes = {
  userInfo: T.object.isRequired,
  updateUserInfo: T.func.isRequired,
};

export default connect(
  (state) => ({
    userInfo: state.userInfo,
    appLocaleCode: getAppLocaleCode(state),
    hasPrimaryLocation: userLocationsSelectors.hasPrimaryLocation(state),
    primaryLocation: userLocationsSelectors.getPrimaryLocation(state),
  }),
  (dispatch) => ({
    updateUserInfo: (params) => dispatch(userOperations.updateUserInfo(params)),
    sendEmailVerification: (email) => dispatch(userOperations.sendEmailVerification(email)),
  }),
)(EditUserInfo);
